<script setup lang="ts">
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ref, watch } from 'vue'
import {Channel} from "@/types/Channels"

const props = defineProps<{
  channel: Channel | null
  open: boolean
}>()

const emit = defineEmits(['update:open', 'save'])

const form = ref<Partial<Channel>>({})

watch(() => props.channel, (newVal) => {
  if (newVal) {
    form.value = { ...newVal }
  }
}, { immediate: true })

const statusOptions = [
  { value: "0", label: 'Disabled' },
  { value: "1", label: 'Active' },
  { value: "2", label: 'Pending' },
  { value: "3", label: 'Archived' }
]

const visibilityOptions = [
  { value: "0", label: 'Hidden' },
  { value: "1", label: 'Visible' },
  { value: "2", label: 'Restricted' }
]

const triggerEventOptions = [
  { value: 'default', label: 'Default' },
  { value: 'manual', label: 'Manual' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'approval', label: 'Approval' },
  { value: 'uploadcomplete', label: 'Upload Complete' }
]

const defaultSortOptions = [
  { value: 'upload DESC', label: 'Upload Date (Newest)' },
  { value: 'upload ASC', label: 'Upload Date (Oldest)' },
  { value: 'name ASC', label: 'Name (A-Z)' },
  { value: 'name DESC', label: 'Name (Z-A)' },
  { value: 'views DESC', label: 'Most Viewed' },
  { value: 'rating DESC', label: 'Highest Rated' }
]

const saveChannel = () => {
  emit('save', form.value)
  emit('update:open', false)
}
</script>

<template>
  <Dialog :open="open" @update:open="(val) => emit('update:open', val)">
    <DialogContent class="sm:max-w-[800px] max-h-[90vh] overflow-y-auto !animate-none">
      <DialogHeader>
        <DialogTitle>
          {{ channel?.id ? 'Edit Channel' : 'Create Channel' }}
          <Badge v-if="channel?.id" variant="outline" class="ml-2">
            ID: {{ channel.id }}
          </Badge>
        </DialogTitle>
      </DialogHeader>

      <div class="grid gap-4 py-4">
        <div class="grid grid-cols-2 gap-4">
          <!-- Basic Information -->
          <div class="space-y-2">
            <Label for="name">Channel Name</Label>
            <Input id="name" v-model="form.name" placeholder="Enter channel name" />
          </div>

          <div class="space-y-2">
            <Label for="shortname">Short Name</Label>
            <Input id="shortname" v-model="form.shortname" placeholder="Enter short name" />
          </div>
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div class="space-y-2">
            <Label for="status">Status</Label>
            <Select v-model="form.status">
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in statusOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="space-y-2">
            <Label for="visibility">Visibility</Label>
            <Select v-model="form.visibility">
              <SelectTrigger>
                <SelectValue placeholder="Select visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in visibilityOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="space-y-2">
            <Label for="defaultsort">Default Sort</Label>
            <Select v-model="form.defaultsort">
              <SelectTrigger>
                <SelectValue placeholder="Select sort order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="option in defaultSortOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div class="space-y-2">
          <Label for="description">Description</Label>
          <Textarea id="description" v-model="form.description" placeholder="Enter channel description" rows="3" />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <Label for="emailaddress">Email Address</Label>
            <Input id="emailaddress" v-model="form.emailaddress" type="email" placeholder="channel@example.com" />
          </div>

          <div class="space-y-2">
            <Label for="parentchannel">Parent Channel ID</Label>
            <Input id="parentchannel" v-model.number="form.parentchannel" type="number" placeholder="0" />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <Label for="modemailaccepted">Approval Email Template</Label>
            <Input id="modemailaccepted" v-model="form.modemailaccepted" placeholder="Template ID" />
          </div>

          <div class="space-y-2">
            <Label for="modemaildenied">Rejection Email Template</Label>
            <Input id="modemaildenied" v-model="form.modemaildenied" placeholder="Template ID" />
          </div>
        </div>

        <!-- Advanced Settings Section -->
        <div class="border-t pt-4 mt-4">
          <h3 class="font-medium text-lg">Advanced Settings</h3>
          
          <div class="grid grid-cols-3 gap-4 mt-2">
            <div class="space-y-2">
              <Label for="triggerevent">Trigger Event</Label>
              <Select v-model="form.triggerevent">
                <SelectTrigger>
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="option in triggerEventOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-2">
              <Label for="delete_media_interval">Auto-delete Interval (days)</Label>
              <Input id="delete_media_interval" v-model.number="form.delete_media_interval" type="number" placeholder="-1 (disabled)" />
            </div>

            <div class="space-y-2">
              <Label for="delete_media_moderationstatus">Auto-delete Status</Label>
              <Input id="delete_media_moderationstatus" v-model.number="form.delete_media_moderationstatus" type="number" placeholder="-1 (disabled)" />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 mt-2">
            <div class="space-y-2">
              <Label for="media_notification_template">Media Notification Template</Label>
              <Input id="media_notification_template" v-model="form.media_notification_template" placeholder="Template ID" />
            </div>

            <div class="space-y-2">
              <Label for="thread_notification_template">Thread Notification Template</Label>
              <Input id="thread_notification_template" v-model="form.thread_notification_template" placeholder="Template ID" />
            </div>
          </div>

          <div class="space-y-2 mt-2">
            <Label for="slackdest">Slack Destination</Label>
            <Input id="slackdest" v-model="form.slackdest" placeholder="Slack webhook or channel" />
          </div>

          <div class="space-y-2 mt-2">
            <Label for="gptsettings">GPT Settings (JSON)</Label>
            <Textarea id="gptsettings" v-model="form.gptsettings" placeholder="Enter JSON configuration" rows="3" />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="emit('update:open', false)">
          Cancel
        </Button>
        <Button type="submit" @click="saveChannel">
          Save Changes
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
